import { useState } from "react";
import { X } from "lucide-react";
import { Button, DialogCancel, Icon, SegmentedControl, Sheet, useRequestClose } from "@cntrl/ui";
import { useShow } from "../../app/hooks/useShows.js";
import { EditorFooterProvider } from "./EditorFooterSlot.js";
import { EditorSaveProvider, useSaveRegistry } from "./EditorSaveRegistry.js";
import { MobileEditorSections, type MobileEditorTab } from "./ShowWizard.js";
import { formatDay } from "../org/rosterFormat.js";
import styles from "./MobileShowEditor.module.scss";

const TABS: { value: MobileEditorTab; label: string }[] = [
  { value: "basics", label: "Basics" },
  { value: "lineup", label: "Lineup" },
  { value: "assets", label: "Assets" },
  { value: "terms", label: "Terms" },
];

// No footer hosts: the phone editor carries its own Save below, and the Terms defaults option is a checkbox in
// the body rather than a footer link.
const NO_HOSTS = { save: null, extra: null };

/**
 * The PHONE show editor (ART-358): a tall sheet with the four sections a booking agent owns — Basics, Lineup,
 * Assets, Terms — as tabs, over the same section components and the same one-Save fan-out the desktop editor
 * uses. Members, Run of show and the rest stay on desktop. Cancel (or any other way out) asks before
 * discarding; Save commits every changed tab and returns to the show sheet.
 */
export function MobileShowEditor({ showId, stageLabel, initialTab = "basics", onClose, onSaved }: {
  showId: string;
  /** "Hold", "Ready", "Confirmed" — the header's leading word. */
  stageLabel: string;
  initialTab?: MobileEditorTab;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Sheet title="Edit show" onClose={onClose} scroll="inner" className={styles.sheet} dirty="auto">
      <EditorFooterProvider hosts={NO_HOSTS}>
        <EditorSaveProvider>
          <EditorBody showId={showId} stageLabel={stageLabel} initialTab={initialTab} onSaved={onSaved} />
        </EditorSaveProvider>
      </EditorFooterProvider>
    </Sheet>
  );
}

function EditorBody({ showId, stageLabel, initialTab, onSaved }: {
  showId: string;
  stageLabel: string;
  initialTab: MobileEditorTab;
  onSaved: () => void;
}) {
  const show = useShow(showId);
  const detail = show.data?.data;
  const registry = useSaveRegistry();
  const requestClose = useRequestClose();
  const [tab, setTab] = useState<MobileEditorTab>(initialTab);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);

  const place = [detail?.city, detail?.state].filter(Boolean).join(", ");
  const sub = [stageLabel, detail?.date ? formatDay(detail.date) : null, place || null].filter(Boolean).join(" · ");

  async function save() {
    if (!registry) return;
    setBusy(true);
    setFailed([]);
    try {
      const { failures } = await registry.saveAll();
      // A partial save keeps the editor open with the tabs that did not land named — never a silent close.
      if (failures.length > 0) setFailed(failures);
      else onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.frame}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h2 className={styles.title}>{detail?.venueName || "Untitled show"}</h2>
          {sub && <p className={styles.sub}>{sub}</p>}
        </div>
        <button type="button" className={styles.close} aria-label="Close editor" onClick={() => requestClose?.()}>
          <Icon icon={X} size={20} />
        </button>
      </header>
      <div className={styles.tabs}>
        <SegmentedControl label="Show sections" value={tab} onChange={setTab} options={TABS} stretch />
      </div>
      <div className={styles.body}>
        <MobileEditorSections showId={showId} tab={tab} onExit={() => requestClose?.()} />
      </div>
      <footer className={styles.footer}>
        {failed.length > 0 && <p className={styles.error} role="alert">{failed.join(", ")} didn’t save.</p>}
        <div className={styles.actions}>
          <DialogCancel disabled={busy} />
          <Button loading={busy} onClick={() => void save()}>Save</Button>
        </div>
      </footer>
    </div>
  );
}
